/**
 * Which statement types a run generates.
 *
 * One run used to cover the whole book: the generator selected every serviced loan and the builder
 * then picked the payment, construction or line-of-credit layout per loan, so "run payment
 * statements" printed all three. This is the include-side control that lets a servicer bill payment
 * statements on the 20th, construction on the 25th and lines on the 30th, as three schedules.
 *
 * ⚠️ Selecting NOTHING means EVERY type, not none. A schedule saved before the split carries no
 * scope and has to keep statementing the whole book, so an empty list cannot mean "no loans" -
 * hence the hint, which says it rather than leaving the operator to find out on the 20th.
 *
 * ⚠️ There is no RESPA option, deliberately. `respaLoan` swaps the payment statement's layout for
 * the Regulation Z one; it does not change what is billed or when, so a RESPA loan is a Payment
 * loan. An option here would strand those borrowers in no run at all.
 */
export const STATEMENT_TYPE_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  { value: 'Payment', label: 'Payment', hint: 'Dutch loans' },
  { value: 'Construction', label: 'Construction', hint: 'non-Dutch, draw-based' },
  { value: 'LineOfCredit', label: 'Line of Credit', hint: 'revolving facilities' },
]

export function StatementTypeScope({
  statementTypes,
  onChangeStatementTypes,
  className = '',
}: {
  statementTypes: string[]
  onChangeStatementTypes: (values: string[]) => void
  className?: string
}) {
  const toggle = (value: string) =>
    onChangeStatementTypes(
      statementTypes.includes(value) ? statementTypes.filter((v) => v !== value) : [...statementTypes, value],
    )

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
        <span>
          Statement Types <span className="text-gray-400">(optional: none selected runs every type)</span>
        </span>
        {statementTypes.length > 0 && (
          <span className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            {statementTypes.length} selected
            <button
              type="button"
              className="text-shade-blue hover:underline"
              onClick={() => onChangeStatementTypes([])}
            >
              Clear
            </button>
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-gray-200 p-1.5">
        {STATEMENT_TYPE_OPTIONS.map(({ value, label, hint }) => {
          const on = statementTypes.includes(value)
          return (
            <button
              key={value}
              type="button"
              title={hint}
              onClick={() => toggle(value)}
              className={`h-fit rounded-full border px-2 py-0.5 text-xs ${
                on
                  ? 'border-shade-blue bg-shade-blue text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-shade-blue'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>
      <p className="mt-1 text-xs text-gray-400">
        Resolved from each loan&rsquo;s own servicing record, by the same rule that decides which statement it prints,
        so a loan cannot be picked for one run and printed another&rsquo;s statement. A Line of Credit run only finds
        loans whose Facility Type is set to Revolving Line of Credit.
      </p>
    </div>
  )
}
